import React, { useState, useEffect, useRef } from 'react'
import { api, formatCurrency } from '../../utils/api'
import SearchLinks from '../shared/SearchLinks'
import { toPence, toPounds, roundDownToX9, isCompareAtBlocked, swingPct, formatLastRepriced } from './repriceMath'

// Fast per-item reprice queue over a set of selected products. One card at a
// time; each apply is a real D2C reprice (identical to the Edit modal) via
// POST /api/pricing/reprice, then auto-advances. Keyboard: Enter=apply, Esc=exit;
// P/M/9/S/B act when focus is outside the price box (the on-screen buttons always
// work). See docs/specs_20260710_reprice_review_queue.md.

const BLOCK_MSG = 'Either set a higher compare-at price OR delete the value.'
const SWING_WARN = 0.2

// A labelled value tile — small uppercase label over a large tabular value, with
// an optional sub-line. `accent` lifts the recommended (proposed) tile out.
function StatTile({ label, value, sub, valueColor, subColor, accent }) {
  return (
    <div style={{
      border: `1px solid ${accent ? 'rgba(59, 130, 246, 0.35)' : 'var(--border-color)'}`,
      background: accent ? 'rgba(59, 130, 246, 0.06)' : 'transparent',
      borderRadius: 10,
      padding: '10px 14px',
      minWidth: 0,
    }}>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 5 }}>
        {label}
      </div>
      <div style={{ fontSize: 23, fontWeight: 700, lineHeight: 1.05, fontVariantNumeric: 'tabular-nums', color: valueColor || 'inherit' }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, marginTop: 4, fontVariantNumeric: 'tabular-nums', color: subColor || '#9ca3af' }}>
          {sub}
        </div>
      )}
    </div>
  )
}

export default function RepriceReviewOverlay({ items, onClose, onApplied }) {
  const [index, setIndex] = useState(0)
  const [priceStr, setPriceStr] = useState('')
  const [compareAtStr, setCompareAtStr] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [applied, setApplied] = useState(0)
  const [skipped, setSkipped] = useState(0)

  const item = items[index]
  const shopify = item?.byChannel?.shopify || {}
  const currentCents = shopify.current_cents ?? null
  const proposedCents = shopify.proposed_cents ?? null
  const feeRate = shopify.fee_rate ?? null
  const spCents = item?.sp_cents ?? null
  const costCents = item?.cost_cents ?? null
  const floorCents = item?.floor_cents ?? null
  const existingCompareAt = item?.compare_at_cents ?? null

  // Both boxes pre-fill with the CURRENT price whenever the item changes — the
  // exact default the Edit modal uses.
  useEffect(() => {
    setPriceStr(toPounds(currentCents))
    setCompareAtStr(toPounds(currentCents))
    setError(null)
  }, [index, currentCents])

  const priceCents = toPence(priceStr)
  const compareAtCents = toPence(compareAtStr)
  const blocked = isCompareAtBlocked(priceCents, compareAtCents)
  const invalid = priceCents == null || priceCents <= 0 || blocked
  const compareAtChanged = compareAtCents !== existingCompareAt
  // Net margin after the channel fee at the price in the box (below-cost signal).
  const liveMargin =
    priceCents != null && costCents != null && feeRate != null
      ? Math.round(priceCents * (1 - feeRate)) - costCents
      : null
  // Proposed vs current, for the Proposed tile's delta line + colour.
  const delta = proposedCents != null && currentCents != null ? proposedCents - currentCents : null
  const deltaPct = delta != null && currentCents ? Math.round((delta / currentCents) * 100) : null
  const proposedColor = delta == null || delta === 0 ? undefined : delta < 0 ? '#16a34a' : '#d97706'

  function advance() {
    if (index < items.length - 1) setIndex(index + 1)
    else onClose?.()
  }

  async function applyAndNext() {
    if (invalid || busy) return
    setError(null)
    if (costCents != null && priceCents < costCents) {
      if (!window.confirm(
        `${formatCurrency(priceCents / 100)} is below cost ${formatCurrency(costCents / 100)} — proceed?`,
      )) return
    }
    const sw = swingPct(currentCents, priceCents)
    if (sw != null && sw >= SWING_WARN) {
      if (!window.confirm(
        `${formatCurrency(currentCents / 100)} → ${formatCurrency(priceCents / 100)} is ${Math.round(sw * 100)}% — sure?`,
      )) return
    }
    const clear = compareAtCents == null
    setBusy(true)
    try {
      await api.post('/api/pricing/reprice', {
        shopify_product_id: item.shopify_product_id,
        new_price_cents: priceCents,
        compare_at_cents: clear ? null : compareAtCents,
        clear_compare_at: clear,
      })
      setApplied(a => a + 1)
      onApplied?.(item.shopify_product_id)
      advance()
    } catch (e) {
      setError(e.detail || e.message || 'Failed to reprice')
    } finally {
      setBusy(false)
    }
  }

  function skip() { setSkipped(s => s + 1); advance() }
  function back() { if (index > 0) setIndex(index - 1) }
  function fillProposed() { if (proposedCents != null) setPriceStr(toPounds(proposedCents)) }
  function fillMarket() { if (spCents != null) setPriceStr(toPounds(spCents)) }
  function fillRoundX9() {
    const r = roundDownToX9(priceCents ?? currentCents)
    if (r != null) setPriceStr(toPounds(r))
  }

  // Stable window key handler reading the latest actions via a ref.
  const handlers = useRef({})
  handlers.current = { applyAndNext, skip, back, fillProposed, fillMarket, fillRoundX9 }
  useEffect(() => {
    function onKey(e) {
      const h = handlers.current
      if (e.key === 'Escape') { onClose?.(); return }
      if (e.key === 'Enter') { e.preventDefault(); h.applyAndNext(); return }
      const t = e.target
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return
      const k = e.key.toLowerCase()
      if (k === 'a') h.applyAndNext()
      else if (k === 'p') h.fillProposed()
      else if (k === 'm') h.fillMarket()
      else if (k === '9') h.fillRoundX9()
      else if (k === 's' || e.key === 'ArrowRight') h.skip()
      else if (k === 'b' || e.key === 'ArrowLeft') h.back()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  if (!item) return null

  const pounds = (c) => formatCurrency(c != null ? c / 100 : null)

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ width: 'min(640px, 92vw)', maxWidth: 'min(640px, 92vw)' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Reprice review — {index + 1} / {items.length}</h3>
          <span style={{ fontSize: 12, color: '#6b7280' }}>{applied} repriced · {skipped} skipped</span>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body-scroll">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>{item.title || item.shopify_product_id}</span>
            {item.age_months != null && (
              <span style={{ fontSize: 11, color: '#6b7280', border: '1px solid var(--border-color)', borderRadius: 999, padding: '1px 8px' }}>
                {item.age_months} mo
              </span>
            )}
            {item.last_repriced_at
              ? <span style={{ fontSize: 11, color: '#b45309', background: 'rgba(217, 119, 6, 0.12)', borderRadius: 999, padding: '1px 10px', fontWeight: 500 }}>
                  Last repriced {formatLastRepriced(item.last_repriced_at)}
                </span>
              : <span style={{ fontSize: 11, color: '#9ca3af', border: '1px solid var(--border-color)', borderRadius: 999, padding: '1px 10px' }}>
                  Never repriced
                </span>}
          </div>

          {/* Key values as tiles — big and legible; Proposed is accented. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(128px, 1fr))', gap: 10, marginBottom: 16 }}>
            <StatTile label="Cost" value={pounds(costCents)} sub={floorCents != null ? `floor ${pounds(floorCents)}` : null} />
            <StatTile label="Market" value={pounds(spCents)} />
            <StatTile label="Current" value={pounds(currentCents)} />
            <StatTile
              label="Proposed"
              value={pounds(proposedCents)}
              accent
              valueColor={proposedColor}
              subColor={proposedColor}
              sub={delta != null && delta !== 0
                ? `${delta < 0 ? '▼' : '▲'} ${pounds(Math.abs(delta))}${deltaPct != null ? ` · ${deltaPct > 0 ? '+' : ''}${deltaPct}%` : ''}`
                : null}
            />
          </div>

          {item.has_override && (
            <div style={{ background: 'rgba(168, 85, 247, 0.12)', color: '#a855f7', padding: '6px 10px', borderRadius: 4, fontSize: 12, marginBottom: 12 }}>
              🔒 <strong>Fixed price</strong> — pinned; applying re-pins at the new price.
            </div>
          )}

          <div className="form-group">
            <label htmlFor="reprice-price">New price (£)</label>
            <input
              id="reprice-price" aria-label="New price (£)"
              type="number" step="0.01" min="0" inputMode="decimal"
              value={priceStr} onChange={e => setPriceStr(e.target.value)} autoFocus
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-sm btn-secondary" onClick={fillProposed} disabled={proposedCents == null}>Proposed (P)</button>
              <button type="button" className="btn btn-sm btn-secondary" onClick={fillMarket} disabled={spCents == null}>Market (M)</button>
              <button type="button" className="btn btn-sm btn-secondary" onClick={fillRoundX9}>Round £x9 (9)</button>
            </div>
            {liveMargin != null && (
              <div style={{ fontSize: 11, marginTop: 6, color: liveMargin < 0 ? '#ef4444' : '#6b7280' }}>
                Margin after fee: <strong>{pounds(liveMargin)}</strong>
                {floorCents != null && priceCents != null && priceCents < floorCents && (
                  <span style={{ color: '#ef4444', marginLeft: 8 }}>· below floor</span>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="reprice-compareat">Compare-at price (£)</label>
            <input
              id="reprice-compareat" aria-label="Compare-at price (£)"
              type="number" step="0.01" min="0" inputMode="decimal"
              value={compareAtStr} onChange={e => setCompareAtStr(e.target.value)}
              placeholder="blank = remove (take off sale)"
            />
            <div style={{ fontSize: 12, color: '#374151', marginTop: 6 }}>
              Existing on Shopify: <strong>{existingCompareAt != null ? pounds(existingCompareAt) : '— (not on sale)'}</strong>
              {compareAtChanged && <span style={{ color: '#d97706', fontWeight: 600, marginLeft: 8 }}>· proposing a change</span>}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
              Setting a compare-at puts the item in the SALE collection; clearing it takes it out.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              {existingCompareAt != null && compareAtCents !== existingCompareAt && (
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setCompareAtStr(toPounds(existingCompareAt))}>
                  Keep current compare-at price
                </button>
              )}
              {compareAtStr !== '' && (
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setCompareAtStr('')}>Clear</button>
              )}
            </div>
          </div>

          {blocked && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>{BLOCK_MSG}</div>}
          {error && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }} role="alert">{error}</div>}

          <div style={{ marginTop: 10 }}><SearchLinks title={item.title} order={['google', 'ebaySold', 'ebayActive', 'reverbSold']} /></div>
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button type="button" className="btn btn-sm btn-secondary" onClick={back} disabled={index === 0 || busy}>← Back (B)</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-sm btn-secondary" onClick={skip} disabled={busy}>Skip (S) →</button>
            <button type="button" className="btn btn-sm btn-primary" onClick={applyAndNext} disabled={invalid || busy}>
              {busy ? 'Applying…' : 'Apply & next (⏎)'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
